/**
 * Created by Ning Jiang on 2021-05-07.
 */

import {Layers, SerialLayers} from "../view/SerialLayers";
import {SerialSymbol} from "../symbol/SerialSymbol";
import {SerialSymbolConfig} from "../symbol/SerialSymbolConfig";
import {ReelState} from "../../core/reel/ReelState";
import {gsap} from "gsap";
import {Serial} from "../Serial";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {ReelPositionController} from "./ReelPositionController";

export class SerialReelView {

    public readonly reelId:number;
    public readonly positionId:number;
    public readonly defaultHeight:number = ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 3;
    public readonly allSymbols:SerialSymbol[] = [];

    protected readonly _lowContainer:PIXI.Container;
    protected readonly _highContainer:PIXI.Container;
    protected readonly _lowWinContainer:PIXI.Container;
    protected readonly _highWinContainer:PIXI.Container;

    public currentHeight:number;
    public currentTop:number;
    public wantBounce:boolean = false;
    public spinStepOffset:number = 0;
    private _lastStepOffset:number;
    public nextSpinStepConfig:{
        reelState?:ReelState,
        duration?:number,
        ease?:string
    } | null;
    private _symbolsNumTop:number;
    private _stepDuration:number;
    private _stepEase:string = "none";
    private _currentTl:TimelineLite;
    private _positionsSnapshot:number[];
    private _isPositionSnapshotDirty:boolean = true;

    public get stepDistance():number {
        return this.allSymbols[this._symbolsNumTop].symbolStep;
    }

    public get currentStepLeftTime():number {
        if(this._currentTl != null && this._currentTl.isActive()) {
            return this._currentTl.duration() * (1 - this._currentTl.progress());
        }
        return 0;
    }

    public get currentStepDuration():number {
        if(this._currentTl) {
            return this._currentTl.duration();
        }
        return 0;
    }

    constructor(reelId:number, positionId:number, initHeight:number) {
        this.reelId = reelId;
        this.positionId = positionId;
        this.currentHeight = initHeight;
        this.currentTop = ReelPositionController.REEL_AREAS[reelId].y;

        this._lowContainer = this.createSymbolContainer();
        SerialLayers.getLayer(Layers.LOW_SYMBOLS)?.addChild(this._lowContainer);
        this._highContainer = this.createSymbolContainer();
        SerialLayers.getLayer(Layers.HIGH_SYMBOLS)?.addChild(this._highContainer);
        this._lowWinContainer = this.createSymbolContainer();
        SerialLayers.getLayer(Layers.LOW_SYMBOLS_WIN)?.addChild(this._lowWinContainer);
        this._highWinContainer = this.createSymbolContainer();
        SerialLayers.getLayer(Layers.HIGH_SYMBOLS_WIN)?.addChild(this._highWinContainer);
    }

    private createSymbolContainer():PIXI.Container {
        const container = new PIXI.Container();
        container.position.set(ReelPositionController.REEL_AREAS[this.reelId].x, this.currentTop);
        this.setContainerMask(container);
        return container;
    }

    private setContainerMask(container:PIXI.Container):void {
        if(container.mask) {
            const oldMask = <PIXI.Graphics>container.mask;
            container.mask = null;
            container.removeChild(oldMask);
            oldMask.destroy();
        }

        const mask:PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 1);
        mask.drawRect(-ReelPositionController.REEL_AREAS[this.reelId].width * 0.5, 0, ReelPositionController.REEL_AREAS[this.reelId].width, this.currentHeight);
        mask.endFill();

        container.addChild(mask);
        container.mask = mask;
    }

    public addSymbol(symbol:SerialSymbol):void {
        if(SerialSymbolConfig.SYMBOLS[symbol.stackedSymbolName?.symName].highSymbol) {
            this._highContainer.addChild(symbol);
        } else {
            this._lowContainer.addChild(symbol);
        }
    }

    public addWinSymbol(symbol:SerialSymbol):void {
        if(SerialSymbolConfig.SYMBOLS[symbol.stackedSymbolName?.symName].highSymbol) {
            this._highWinContainer.addChild(symbol);
        } else {
            this._lowWinContainer.addChild(symbol);
        }
    }

    public removeSymbol(symbol:SerialSymbol):void {
        if(symbol.parent) {
            symbol.parent.removeChild(symbol);
        }
    }

    public initSymbolsPosition(symbolsNumTop:number):void {
        this._symbolsNumTop = symbolsNumTop;
        let offset:number = 0;
        for(let i = symbolsNumTop; i < this.allSymbols.length; i++) {
            this.allSymbols[i].y = offset + this.allSymbols[i].symbolHeight * 0.5;
            offset += this.allSymbols[i].symbolStep;
        }
        if(symbolsNumTop > 0) {
            offset = 0;
            for(let i = symbolsNumTop - 1; i >= 0; i--) {
                this.allSymbols[i].y = offset - this.allSymbols[i].bottomPadding - this.allSymbols[i].symbolHeight * 0.5;
                offset -= this.allSymbols[i].symbolStep;
            }
        }
    }

    private updatePositionSnapshot():void {
        // To snapshot the symbols start posion for each step. it's not sure if the onUpdate or onStart triggered first, so need to trigger this one in both places.
        if(this._isPositionSnapshotDirty) {
            this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].y);
            this._isPositionSnapshotDirty = false;
            this._lastStepOffset = 0;
        }
    }

    public step(newSymbol:SerialSymbol, removeSymbolCallback:() => void, stepDistance?:number,):TimelineLite {
        const reelState:ReelState | undefined = this.nextSpinStepConfig?.reelState;
        const stepDuration:number = this.nextSpinStepConfig?.duration ? this.nextSpinStepConfig.duration : this._stepDuration;
        const stepEase:string = this.nextSpinStepConfig?.ease ? this.nextSpinStepConfig.ease : this._stepEase;
        if(stepDistance == null) {
            stepDistance = this.stepDistance; // stepDistance is the size of first coming symbol.
        }
        this.nextSpinStepConfig = null;

        this._currentTl = gsap.timeline();
        this._currentTl.add(() => {
            if(reelState) {
                this.setReelState(reelState);
            }
            this._stepDuration = stepDuration;
            this._stepEase = stepEase;
            this.spinStepOffset = 0;
            this.updatePositionSnapshot();
        });
        this._currentTl.add(gsap.to(this,
            {
                duration : stepDuration,
                ease : stepEase,
                spinStepOffset : stepDistance,
                onUpdate : () => {
                    this.allSymbols.forEach((symbol, index) => {
                        symbol.y = this._positionsSnapshot[index] + this.spinStepOffset;
                    });
                    if(this.wantBounce && this.spinStepOffset < this._lastStepOffset) {
                        this.doBounce();
                        return;
                    }
                    this._lastStepOffset = this.spinStepOffset;
                }
            }));
        this._currentTl.add(() => {
            this._isPositionSnapshotDirty = true;
            removeSymbolCallback();
            if(this.wantBounce) {
                this.doBounce();
            }
        })

        return this._currentTl;
    }

    private doBounce():void {
        this.wantBounce = false;
        this.setReelState(ReelState.BOUNCE);
    }

    private setReelState(state:ReelState):void {
        switch(this.positionId) {
            case 0:
            case 1:
            case 5:
            case 6:
                Serial.enhancerReels[this.reelId][this.positionId].reelState = state;
                break;
            default:
                Serial.reels[this.reelId].reelState = state;
        }
    }

    public addSymbolToOverlay(symbol:SerialSymbol, globalPosition:PIXI.Point):void {
        symbol.position.set(globalPosition.x, globalPosition.y);
        if(SerialSymbolConfig.SYMBOLS[symbol.stackedSymbolName?.symName].highSymbol) {
            SerialLayers.getLayer(Layers.HIGH_SYMBOLS_STICKY)?.addChild(symbol);
        } else {
            SerialLayers.getLayer(Layers.LOW_SYMBOLS_STICKY)?.addChild(symbol);
        }
    }

    public resetPosition(top:number):void {
        if(top != this.currentTop) {
            this.currentTop = top;
            this._lowContainer.y = this.currentTop;
            this._highContainer.y = this.currentTop;
            this._lowWinContainer.y = this.currentTop;
            this._highWinContainer.y = this.currentTop;
        }
    }

    public resetHeight(height?:number):void {
        if(!height) {
            height = this.currentHeight;
        }
        if(height != this.currentHeight) {
            this.currentHeight = height;
            this.setContainerMask(this._lowContainer);
            this.setContainerMask(this._highContainer);
            this.setContainerMask(this._lowWinContainer);
            this.setContainerMask(this._highWinContainer);
            this.resizeSymbolHeight();
            this.initSymbolsPosition(this._symbolsNumTop);
        }
    }

    private resizeSymbolHeight():void {
        const symHeight = this.getSymbolHeight();
        this.allSymbols.forEach(sym => {
            sym.symbolHeight = symHeight;
        });
    }

    public getSymbolHeight():number {
        return this.currentHeight / 3;
    }
}